import {
  sort,
  SortState,
  SortOption,
  Direction,
  SortOptionSelectItem,
} from '../../utils/sort';
import { MedisanteTargetSystem, TargetSystemsSortBy } from './types';
import { MedisanteDevice } from '../Device/types';
import { Org } from '../../contexts/AuthProvider/types';

export const targetSystemsSortingReducer = (
  state: SortState<MedisanteTargetSystem>,
  action: SortOption<TargetSystemsSortBy>
): SortState<MedisanteTargetSystem> => {
  const getDirectionByPrevious = (direction?: Direction) => {
    return direction === Direction.ASC ? Direction.DESC : Direction.ASC;
  };

  switch (action.sortBy) {
    case TargetSystemsSortBy.NAME:
      return {
        name: action.direction || getDirectionByPrevious(state.name),
      };
    case TargetSystemsSortBy.URL:
      return {
        url: action.direction || getDirectionByPrevious(state.url),
      };
    case TargetSystemsSortBy.DEVICECOUNT:
      return {
        deviceCount:
          action.direction || getDirectionByPrevious(state.deviceCount),
      };
    case TargetSystemsSortBy.LASTSTATUSCODE:
      return {
        lastStatusCode:
          action.direction || getDirectionByPrevious(state.lastStatusCode),
      };
    case TargetSystemsSortBy.REQUESTS:
      return {
        count: action.direction || getDirectionByPrevious(state.count),
      };
    case TargetSystemsSortBy.REQUESTS_TEST:
      return {
        testCount: action.direction || getDirectionByPrevious(state.testCount),
      };
    default:
      return state;
  }
};

export const sortTargetSystems = (
  targetSystems: MedisanteTargetSystem[],
  sortState: SortState<MedisanteTargetSystem>,
  deviceCount: (tsId: string) => TargetSystemDeviceInfo
) =>
  sort([...targetSystems], sortState, {
    name: (u) => u.name,
    url: (u) => u.url,
    deviceCount: (u) => {
      const count = deviceCount(u.id);
      return count ? count.count : 0;
    },
    lastStatusCode: (u) => u.lastStatusCode || 0,
  });

export const notFullySuportedModel = [
  'IR42',
  'PO200',
  'PM100',
  'GW9017',
  'SmartLink',
  'GTEL',
  'W550',
  'FORA6',
  'IR20',
  'P80',
  'MS6110',
  'AOJ-25A',
  'BM1000',
  'PC-60NW',
];

export const onlyCareDevices = ['BP800', 'BG800', 'BC800'];

export interface TargetSystemDeviceInfo {
  incompatibile: boolean;
  count: number;
}

export const deviceForTargetSystemInfo = (
  targetSystemID: string,
  orgs: Org[],
  devices: MedisanteDevice[]
): TargetSystemDeviceInfo => {
  const tsDevices = devices.filter((d) => {
    if (d.allowUnlinkedGateway) {
      return false;
    }
    return d.targetSystems.includes(targetSystemID);
  });
  const notSupportedDevices = tsDevices.filter(
    (d) => notFullySuportedModel.indexOf(d.model) !== -1
  );

  return {
    count: tsDevices.length,
    incompatibile: notSupportedDevices.length > 0,
  };
};

export const mobileSortOptions: SortOptionSelectItem<TargetSystemsSortBy>[] = [
  {
    value: { sortBy: TargetSystemsSortBy.NAME, direction: Direction.ASC },
    label: 'Name (ascending)',
    id: 'name-asc',
  },
  {
    value: { sortBy: TargetSystemsSortBy.NAME, direction: Direction.DESC },
    label: 'Name (descending)',
    id: 'name-desc',
  },
  {
    value: { sortBy: TargetSystemsSortBy.URL, direction: Direction.ASC },
    label: 'URL (ascending)',
    id: 'url-asc',
  },
  {
    value: { sortBy: TargetSystemsSortBy.URL, direction: Direction.DESC },
    label: 'URL (descending)',
    id: 'url-desc',
  },
  {
    value: {
      sortBy: TargetSystemsSortBy.DEVICECOUNT,
      direction: Direction.ASC,
    },
    label: 'Device count (ascending)',
    id: 'device-count-asc',
  },
  {
    value: {
      sortBy: TargetSystemsSortBy.DEVICECOUNT,
      direction: Direction.DESC,
    },
    label: 'Device count (descending)',
    id: 'device-count-desc',
  },
  {
    value: {
      sortBy: TargetSystemsSortBy.LASTSTATUSCODE,
      direction: Direction.ASC,
    },
    label: 'Last status code (ascending)',
    id: 'last-status-asc',
  },
  {
    value: {
      sortBy: TargetSystemsSortBy.LASTSTATUSCODE,
      direction: Direction.DESC,
    },
    label: 'Last status code (descending)',
    id: 'last-status-desc',
  },
  {
    value: {
      sortBy: TargetSystemsSortBy.REQUESTS,
      direction: Direction.ASC,
    },
    label: 'Requests (ascending)',
    id: 'requests-asc',
  },
  {
    value: {
      sortBy: TargetSystemsSortBy.REQUESTS,
      direction: Direction.DESC,
    },
    label: 'Requests (descending)',
    id: 'requests-desc',
  },
  {
    value: {
      sortBy: TargetSystemsSortBy.REQUESTS_TEST,
      direction: Direction.ASC,
    },
    label: 'Test Requests (ascending)',
    id: 'requests-test-asc',
  },
  {
    value: {
      sortBy: TargetSystemsSortBy.REQUESTS_TEST,
      direction: Direction.DESC,
    },
    label: 'Test Requests (descending)',
    id: 'requests-test-desc',
  },
];
